<script setup lang="ts">
  import Dialog from '@/components/ui/dialog/Dialog.vue'
  import DialogContent from '@/components/ui/dialog/DialogContent.vue'
  import DialogDescription from '@/components/ui/dialog/DialogDescription.vue'
  import DialogTitle from '@/components/ui/dialog/DialogTitle.vue'
  import DialogTrigger from '@/components/ui/dialog/DialogTrigger.vue'
  import InformationalBanner from '@/components/ui/informational-banner/InformationalBanner.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { DocumentsService } from '@/types/generated'
  import { FileTextIcon, InfoIcon } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'
  import DocumentsList from './DocumentsList.vue'
  import DocumentsUpload from './DocumentsUpload.vue'

  const { t } = useTranslation({
    en: {
      Documents: 'Documents',
      UploadDocuments: 'Upload Documents'
    },
    de: {
      Documents: 'Dokumente',
      UploadDocuments: 'Dokumente hochladen'
    }
  })

  const props = withDefaults(
    defineProps<{
      documentProcessor: any
      title?: string
      note?: string
      showUpload?: boolean
      showDelete?: boolean
      showDownload?: boolean
      showEdit?: boolean
      showRetry?: boolean
      compact?: boolean
      onDocumentDeleted?: (documentId: string) => Promise<void>
      onDocumentRetry?: (documentId: string) => Promise<void>
      onUploadComplete?: () => Promise<void>
    }>(),
    {
      title: 'Documents',
      note: undefined,
      showUpload: true,
      showDelete: true,
      showDownload: true,
      showEdit: false,
      showRetry: true,
      compact: false
    }
  )

  const emit = defineEmits<{
    documentDeleted: [documentId: string]
    documentRetry: [documentId: string]
    fileUploaded: [documentId: string]
    filesSelected: [files: File[]]
    uploadComplete: []
    dialogClose: []
  }>()

  const isOpen = ref(false)

  // Watch for dialog close to emit event
  watch(isOpen, (newValue, oldValue) => {
    if (oldValue === true && newValue === false) {
      emit('dialogClose')
    }
  })

  // Use the provided processor directly
  const documentProcessor = computed(() => props.documentProcessor)

  // Count from processor documents
  const documentCount = computed(() => {
    return documentProcessor.value?.documents?.length || 0
  })

  // Handle events from DocumentsList
  const handleDocumentDeleted = async (documentId: string) => {
    emit('documentDeleted', documentId)
    if (props.onDocumentDeleted) {
      await props.onDocumentDeleted(documentId)
    }
  }

  const handleDocumentRetry = async (documentId: string) => {
    try {
      // Call the retry API
      await DocumentsService.documentsRetryProcessingCreate({
        uuid: documentId
      })

      // Show success message
      genericSuccessToast()

      // Emit event for parent to handle
      emit('documentRetry', documentId)
      if (props.onDocumentRetry) {
        await props.onDocumentRetry(documentId)
      }
    } catch (error) {
      console.error('Error retrying document:', error)
      genericErrorToast()
    }
  }

  // Handle events from DocumentsUpload
  const handleFileUploaded = (documentId: string) => {
    emit('fileUploaded', documentId)
  }

  const handleFilesSelected = (files: File[]) => {
    emit('filesSelected', files)
  }

  const handleUploadComplete = async () => {
    emit('uploadComplete')
    if (props.onUploadComplete) {
      await props.onUploadComplete()
    }
  }
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot name="trigger">
        <button
          type="button"
          class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
            font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
            focus:ring-offset-2"
        >
          <FileTextIcon class="size-4 shrink-0" />
          {{ t('UploadDocuments') }}
        </button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-2xl overflow-hidden p-0">
      <div
        class="w-full"
        data-cy="documents-dialog-content"
      >
        <!-- Header -->
        <div
          class="border-b border-gray-200 px-4 py-3"
          data-cy="documents-dialog-header"
        >
          <DialogTitle class="text-lg font-medium">{{ title }}</DialogTitle>
          <DialogDescription class="sr-only">
            Upload and manage documents for this session
          </DialogDescription>

          <!-- Info Note -->
          <InformationalBanner
            v-if="note"
            :icon="InfoIcon"
            class="mt-3"
          >
            {{ note }}
          </InformationalBanner>
        </div>

        <!-- Content -->
        <div class="flex max-h-[500px] flex-col">
          <!-- Upload Area (Top) -->
          <div
            v-if="showUpload"
            class="border-b border-gray-200 p-4"
          >
            <DocumentsUpload
              :document-processor="documentProcessor"
              :compact="compact"
              @file-uploaded="handleFileUploaded"
              @files-selected="handleFilesSelected"
              @upload-complete="handleUploadComplete"
            />
          </div>

          <!-- Documents List (Scrollable) -->
          <div
            class="flex-1 overflow-y-auto p-4 px-2"
            data-cy="documents-list-container"
          >
            <DocumentsList
              :show-delete="showDelete"
              :show-download="showDownload"
              :show-edit="showEdit"
              :show-retry="showRetry"
              :compact="compact"
              :document-processor="documentProcessor"
              @document-deleted="handleDocumentDeleted"
              @document-retry="handleDocumentRetry"
            />
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
